// The VIEW-AXIS DERIVATION notes (brief §3.1 view row; §5.2 axis bullet). Availability and
// recommendation are PROJECTION flags the owning seam derives from the drafted declarations —
// nothing here decides them (G-182). These helpers only translate a flag into the honest note the
// picker renders under a card: a time-shaped axis that is AVAILABLE is so because its date fields
// are declared, and one that is NOT states exactly which declaration it still needs (a date field
// / a range) — offered honestly, never a disabled mystery. The agent's pick carries no note: its
// WHY is the projection's derivation sentence (the summary), rendered verbatim.

import type { ComposerViewOption, WorkView } from '../projections.ts';

/** What a time-shaped axis derives from: the declaration clause when present, the honest ask when
 *  not. The state-shaped axes (kanban/list) need no date declaration and carry no entry. */
interface AxisDeclaration {
  /** The lowercase clause naming the declaration that makes this axis available. */
  readonly declared: string;
  /** The full sentence naming the missing declaration when the axis is unavailable. */
  readonly missing: string;
}

const TIME_AXIS_DECLARATIONS: Readonly<Partial<Record<WorkView, AxisDeclaration>>> = {
  calendar: {
    declared: 'date field declared',
    missing: 'Needs a date field declared — one calendar date per item.',
  },
  timeline: {
    declared: 'range fields declared',
    missing: 'Needs a range declared — a start and an end date field.',
  },
};

/** The note an axis card renders under its derivation sentence. */
export interface AxisNote {
  readonly kind: 'available' | 'unavailable';
  readonly text: string;
}

/** Derive the card note from the projection flags: an unavailable axis names the declaration it
 *  needs; a non-picked available axis is one tab away (with the declaration that unlocked it, when
 *  the axis is time-shaped); the agent's pick carries none — its summary is the WHY. */
export function axisNote(option: ComposerViewOption): AxisNote | null {
  const declaration = TIME_AXIS_DECLARATIONS[option.view];
  if (!option.available) {
    return { kind: 'unavailable', text: declaration?.missing ?? 'Needs its date fields declared.' };
  }
  if (option.recommended) return null;
  const clause = declaration === undefined ? '' : ` · ${declaration.declared}`;
  return { kind: 'available', text: `Available — one tab away${clause}.` };
}

/** The declaration clause the activation review's View · cadence row appends — the date
 *  declarations present on the drafted definition, read off the available time-shaped axes
 *  (availability ⇔ declaration, per the owning derivation). Null when none are declared. */
export function declaredDatesLine(options: readonly ComposerViewOption[]): string | null {
  const clauses = options
    .filter((option) => option.available)
    .map((option) => TIME_AXIS_DECLARATIONS[option.view]?.declared)
    .filter((clause): clause is string => clause !== undefined);
  return clauses.length > 0 ? clauses.join(' · ') : null;
}
